import { z } from "zod";
const FUNNEL_SESSION_KEY = "garimpa:marketing-funnel:v1";
let clientAttribution: Record<string, string> = {};
/** Browser-only analytics state supplied by the server-rendered viewer. */
export function setClientAttribution(attribution: Record<string, string>): void {
  if (typeof window === "undefined") return;
  clientAttribution = attribution;
}
export const marketingFunnelValueSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9_]+$/);

const marketingFunnelStateSchema = z.object({
  name: marketingFunnelValueSchema,
  variant: marketingFunnelValueSchema.optional(),
  startedAt: z.number().int().nonnegative(),
});

export type MarketingFunnelState = z.infer<typeof marketingFunnelStateSchema>;

export interface MarketingFunnelDefinition {
  name: string;
  variant?: string;
}

function getStorage(kind: "localStorage" | "sessionStorage"): Storage | null {
  if (typeof window === "undefined") return null;

  try {
    return window[kind] ?? null;
  } catch {
    return null;
  }
}

function parseStored<T>(
  kind: "localStorage" | "sessionStorage",
  key: string,
  schema: z.ZodType<T>,
) {
  try {
    const raw = getStorage(kind)?.getItem(key);
    return raw ? (schema.safeParse(JSON.parse(raw)).data ?? null) : null;
  } catch {
    return null;
  }
}

function persist(kind: "localStorage" | "sessionStorage", key: string, value: unknown): void {
  try {
    getStorage(kind)?.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be disabled or full; analytics should never affect the user flow.
  }
}

export function getActiveMarketingFunnel(): MarketingFunnelState | null {
  return parseStored("sessionStorage", FUNNEL_SESSION_KEY, marketingFunnelStateSchema);
}

export function startMarketingFunnel(
  definition: MarketingFunnelDefinition,
): MarketingFunnelState | null {
  const parsed = marketingFunnelStateSchema.safeParse({
    ...definition,
    startedAt: Date.now(),
  });
  if (!parsed.success) return null;

  persist("sessionStorage", FUNNEL_SESSION_KEY, parsed.data);
  return parsed.data;
}

export function getActiveMarketingFunnelParams(): Record<string, string> {
  const state = getActiveMarketingFunnel();
  if (!state) return {};
  if (!state.variant) return { funnel_name: state.name };

  return {
    funnel_name: state.name,
    funnel_variant: state.variant,
  };
}

export function getMarketingContextParams(): Record<string, string> {
  return {
    ...clientAttribution,
    ...getActiveMarketingFunnelParams(),
  };
}
